import type { Band } from "./parse";
import type { PosterTheme } from "./themes";

/**
 * Mise en page de l'affiche, sans dessin : quels noms vont sur quelle ligne, a quel corps.
 * Pure (la mesure du texte est passee en parametre), donc testable sans navigateur.
 *
 * Trois niveaux de corps : les tetes d'affiche (groupes etoiles ou cites plusieurs fois),
 * puis le premier tiers des autres dans l'ordre de saisie, puis le reste. Le corps de base
 * est cherche par dichotomie : le plus grand qui fait tenir toutes les lignes.
 */
export type PosterFormat = "post" | "story";

export interface FormatGeometry {
  width: number;
  height: number;
  /** Haut de la zone du titre. */
  titleTop: number;
  /** Ligne de base de l'adresse du site. */
  footerBaseline: number;
  /** Bas de la zone des noms. */
  bodyBottom: number;
}

export const FORMATS: Record<PosterFormat, FormatGeometry> = {
  post: { width: 1080, height: 1350, titleTop: 92, footerBaseline: 1290, bodyBottom: 1215 },
  story: { width: 1080, height: 1920, titleTop: 250, footerBaseline: 1720, bodyBottom: 1630 },
};

export const MARGIN_X = 80;
export const TITLE_MAX_SIZE = 104;
export const FOOTER_SIZE = 30;
const LINE_HEIGHT = 1.22;
const TIER_GAP = 0.45;
const SEPARATOR = " \u{2022} ";

export type Tier = 0 | 1 | 2;

/** Mesure la largeur d'un texte, en pixels, pour une police, une graisse et un corps. */
export type Measure = (text: string, font: string, weight: number, size: number) => number;

export interface PosterEntry {
  name: string;
  tier: Tier;
  count: number;
}

export interface PosterRow {
  tier: Tier;
  size: number;
  /** Ligne de base. */
  y: number;
  names: string[];
}

export interface PosterLayout {
  format: PosterFormat;
  width: number;
  height: number;
  title: { text: string; size: number; y: number };
  /** Ligne d'ornement sous le titre. */
  ruleY: number;
  rows: PosterRow[];
  footer: { y: number; size: number };
  separator: string;
}

/** Repartit les groupes en trois niveaux. */
export function assignTiers(bands: readonly Band[], starred: ReadonlySet<string>): PosterEntry[] {
  const heads = bands
    .map((band, index) => ({ band, index }))
    .filter(({ band }) => starred.has(band.key) || band.count > 1)
    .sort((a, b) => b.band.count - a.band.count || a.index - b.index)
    .map(({ band }) => band);
  const rest = bands.filter((band) => !(starred.has(band.key) || band.count > 1));
  const midCount = Math.min(18, Math.ceil(rest.length * 0.3));
  const toEntry = (band: Band, tier: Tier): PosterEntry => ({
    name: band.name.toLocaleUpperCase("fr-FR"),
    tier,
    count: band.count,
  });
  return [
    ...heads.map((band) => toEntry(band, 0)),
    ...rest.slice(0, midCount).map((band) => toEntry(band, 1)),
    ...rest.slice(midCount).map((band) => toEntry(band, 2)),
  ];
}

interface RowDraft {
  tier: Tier;
  size: number;
  names: string[];
}

function weightFor(theme: PosterTheme, tier: Tier): number {
  return tier === 2 ? theme.lightWeight : theme.strongWeight;
}

function packTier(
  names: readonly string[],
  tier: Tier,
  size: number,
  theme: PosterTheme,
  measure: Measure,
  maxWidth: number,
): RowDraft[] {
  const weight = weightFor(theme, tier);
  const sepWidth = measure(SEPARATOR, theme.font, weight, size);
  const rows: RowDraft[] = [];
  let current: string[] = [];
  let width = 0;
  for (const name of names) {
    const nameWidth = measure(name, theme.font, weight, size);
    const added = current.length === 0 ? nameWidth : width + sepWidth + nameWidth;
    if (current.length > 0 && added > maxWidth) {
      rows.push({ tier, size, names: current });
      current = [name];
      width = nameWidth;
    } else {
      current.push(name);
      width = added;
    }
  }
  if (current.length > 0) {
    rows.push({ tier, size, names: current });
  }
  // Un nom seul plus large que l'affiche : sa ligne prend un corps plus petit.
  return rows.map((row) => {
    if (row.names.length !== 1) {
      return row;
    }
    const alone = measure(row.names[0] ?? "", theme.font, weight, size);
    return alone > maxWidth ? { ...row, size: (size * maxWidth) / alone } : row;
  });
}

function draftRows(
  entries: readonly PosterEntry[],
  base: number,
  theme: PosterTheme,
  measure: Measure,
  maxWidth: number,
): { rows: RowDraft[]; height: number } {
  const rows: RowDraft[] = [];
  let height = 0;
  let previousTier: Tier | null = null;
  for (const tier of [0, 1, 2] as const) {
    const names = entries.filter((entry) => entry.tier === tier).map((entry) => entry.name);
    if (names.length === 0) {
      continue;
    }
    const size = base * theme.tierScale[tier];
    const tierRows = packTier(names, tier, size, theme, measure, maxWidth);
    if (previousTier !== null) {
      height += base * TIER_GAP * theme.tierScale[tier];
    }
    for (const row of tierRows) {
      height += row.size * LINE_HEIGHT;
    }
    rows.push(...tierRows);
    previousTier = tier;
  }
  return { rows, height };
}

function trackedWidth(text: string, theme: PosterTheme, size: number, measure: Measure): number {
  const letters = Array.from(text).length;
  return (
    measure(text, theme.font, theme.strongWeight, size) +
    theme.titleTracking * size * Math.max(0, letters - 1)
  );
}

export interface LayoutInput {
  entries: readonly PosterEntry[];
  title: string;
  theme: PosterTheme;
  format: PosterFormat;
}

/** Calcule la mise en page complete d'une affiche. */
export function layoutPoster(input: LayoutInput, measure: Measure): PosterLayout {
  const geometry = FORMATS[input.format];
  const maxWidth = geometry.width - 2 * MARGIN_X;
  const titleText = input.title.trim().toLocaleUpperCase("fr-FR");
  const titleUnit = trackedWidth(titleText, input.theme, 100, measure) / 100;
  const titleSize = titleUnit > 0 ? Math.min(TITLE_MAX_SIZE, maxWidth / titleUnit) : TITLE_MAX_SIZE;
  const titleY = geometry.titleTop + titleSize * 0.82;
  const ruleY = titleY + 36;
  const bodyTop = ruleY + 46;
  const bodyHeight = geometry.bodyBottom - bodyTop;

  let low = 4;
  let high = 150;
  let best = draftRows(input.entries, low, input.theme, measure, maxWidth);
  for (let step = 0; step < 22; step += 1) {
    const mid = (low + high) / 2;
    const attempt = draftRows(input.entries, mid, input.theme, measure, maxWidth);
    if (attempt.height <= bodyHeight) {
      low = mid;
      best = attempt;
    } else {
      high = mid;
    }
  }

  let cursor = bodyTop + Math.max(0, (bodyHeight - best.height) / 2);
  let previousTier: Tier | null = null;
  const base = low;
  const rows: PosterRow[] = best.rows.map((row) => {
    if (previousTier !== null && previousTier !== row.tier) {
      cursor += base * TIER_GAP * input.theme.tierScale[row.tier];
    }
    previousTier = row.tier;
    const lineHeight = row.size * LINE_HEIGHT;
    const y = cursor + lineHeight / 2 + row.size * 0.36;
    cursor += lineHeight;
    return { tier: row.tier, size: row.size, y, names: row.names };
  });

  return {
    format: input.format,
    width: geometry.width,
    height: geometry.height,
    title: { text: titleText, size: titleSize, y: titleY },
    ruleY,
    rows,
    footer: { y: geometry.footerBaseline, size: FOOTER_SIZE },
    separator: SEPARATOR,
  };
}

// Largeurs moyennes par caractere (en em), quand aucun canvas ne peut mesurer.
const FONT_WIDTH_FACTOR: Record<string, number> = {
  "Concerts Nuit": 0.78,
  "Concerts Ete": 1,
  "Concerts Atelier": 1.25,
};

/** Estimation grossiere d'une largeur de texte, sans navigateur. */
export function estimateWidth(text: string, font: string, size: number): number {
  let em = 0;
  for (const char of text) {
    if (char === " ") {
      em += 0.28;
    } else if (/[\p{Lu}]/u.test(char)) {
      em += 0.64;
    } else if (/[\p{N}]/u.test(char)) {
      em += 0.56;
    } else if (/[\p{L}]/u.test(char)) {
      em += 0.52;
    } else {
      em += 0.32;
    }
  }
  return em * size * (FONT_WIDTH_FACTOR[font] ?? 1);
}

/**
 * Une fonction de mesure pour la page : le canvas du navigateur quand il existe (avec les
 * polices chargees par fonts.ts), l'estimation sinon. Les largeurs sont mesurees une fois
 * a 100 px puis mises a l'echelle.
 */
export function createMeasure(context: CanvasRenderingContext2D | null): Measure {
  const cache = new Map<string, number>();
  return (text, font, weight, size) => {
    const key = `${font}|${weight}|${text}`;
    let at100 = cache.get(key);
    if (at100 === undefined) {
      if (context) {
        context.font = `${weight} 100px "${font}"`;
        at100 = context.measureText(text).width;
      } else {
        at100 = estimateWidth(text, font, 100);
      }
      cache.set(key, at100);
    }
    return (at100 * size) / 100;
  };
}

/** Le contexte 2D d'un canvas neuf, ou null (jsdom, navigateur sans canvas). */
export function canvasContext(): CanvasRenderingContext2D | null {
  try {
    return document.createElement("canvas").getContext("2d");
  } catch {
    return null;
  }
}
